import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { cn } from "~/lib/utils";
import { getNextAvailableSummitOptions } from "~/lib/functions/summit-registration";

const COUNTDOWN_REFRESH_MS = 60_000;

type SummitStartDate = Date | string | null;
export const normalizeSummitStartTimestamp = (
  startDate: SummitStartDate,
): number | null => {
  if (!startDate) {
    return null;
  }

  const timestamp =
    startDate instanceof Date ? startDate.getTime() : Date.parse(startDate);

  return Number.isNaN(timestamp) ? null : timestamp;
};

export const formatCountdown = (timestamp: number): string | null => {
  const now = Date.now();
  const diff = timestamp - now;

  if (diff <= 0) {
    return null;
  }

  const totalMinutes = Math.floor(diff / 60_000);

  if (totalMinutes <= 0) {
    return "<1m";
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (totalHours > 0) {
    return `${totalHours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

const useSummitCountdownLabel = (startDate: SummitStartDate): string | null => {
  const startTimestamp = useMemo(
    () => normalizeSummitStartTimestamp(startDate),
    [startDate],
  );

  const computeLabel = useCallback(
    () => (startTimestamp ? formatCountdown(startTimestamp) : null),
    [startTimestamp],
  );

  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    const nextLabel = computeLabel();
    setLabel(nextLabel);

    if (!startTimestamp || nextLabel === null) {
      return;
    }

    const intervalId = window.setInterval(() => {
      const updatedLabel = computeLabel();
      setLabel(updatedLabel);

      if (updatedLabel === null) {
        window.clearInterval(intervalId);
      }
    }, COUNTDOWN_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [computeLabel, startTimestamp]);

  return label;
};

export const SummitBanner = () => {
  const { data } = useQuery(getNextAvailableSummitOptions);
  const summit = data?.summit;

  const startDate = summit?.startDate ?? null;
  const countdownLabel = useSummitCountdownLabel(startDate);

  if (!summit?.title) {
    return null;
  }

  return (
    <Link
      to="/summit"
      aria-label="Register for the DreamCoach Summit"
      className={cn(
        "group relative block w-full overflow-hidden bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 text-white shadow-lg transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-700",
      )}
    >
      <div
        className={cn(
          "mx-auto grid w-full max-w-screen-xl gap-3 px-4 py-3 text-center",
          "sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-6 sm:px-6",
        )}
      >
        <span className="text-sm font-semibold leading-6 text-white sm:col-start-1 sm:justify-self-start sm:text-left sm:text-base">
          {summit.title}
        </span>
        {countdownLabel ? (
          <span className="flex items-center justify-center gap-2 text-sm font-medium leading-6 text-white sm:col-start-2 sm:justify-self-center sm:text-base">
            <Clock className="h-4 w-4" aria-hidden />
            <span>Starts in {countdownLabel}</span>
          </span>
        ) : null}
        <span className="flex items-center justify-center gap-2 text-sm font-semibold leading-6 text-white sm:col-start-3 sm:justify-end sm:justify-self-end sm:text-base">
          Register now
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
      <span
        className="absolute inset-x-0 bottom-0 h-0.5 bg-white/25"
        aria-hidden
      />
    </Link>
  );
};
